import { useId, useState } from 'react'
import { Link } from 'react-router-dom'
import { companionEventHref, type CompanionAnswer } from '@/lib/companion'
import { validEvidence, type AggregateEvidence } from '@/lib/aggregate'

export function CompanionEvidence({ answer, evidence, compact = false }: { answer?: CompanionAnswer; evidence?: AggregateEvidence; compact?: boolean }) {
  const [warning, setWarning] = useState(false)
  const tooltipId = useId()
  const raw = evidence ?? answer?.evidence
  if (raw && !validEvidence(raw)) return null
  const from = raw?.interval_from ?? answer?.window?.start
  const to = raw?.interval_to ?? answer?.window?.end
  const windowLabel = from && to ? `${new Date(from).toLocaleString()} — ${new Date(to).toLocaleString()}` : ''
  const ids = raw?.effective_event_ids ?? answer?.eventIds ?? []
  const gaps = raw?.missing_ranges ?? []
  return (
    <span className="inline-flex flex-wrap items-center gap-1 text-caption text-text-secondary">
      <span className="rounded-full border border-divider-subtle px-2 py-0.5">
        {raw || answer?.confidence === 'record-backed' ? 'Record-backed · limited confidence' : 'Insufficient context · not AI'}
      </span>
      {windowLabel && <span className="block w-full">Interval (inclusive, local time): {windowLabel}.</span>}
      {gaps.length > 0 && <>
        <button type="button" aria-label="Missing data warning" aria-expanded={warning} aria-describedby={warning ? tooltipId : undefined}
          onClick={() => setWarning(!warning)} onKeyDown={(e) => { if (e.key === 'Escape') setWarning(false) }}
          className="min-h-11 min-w-11 rounded-full border border-divider-subtle" title="Coverage is incomplete or unknown">⚠</button>
        <span>Coverage is incomplete or unknown.</span>
        {warning && <span id={tooltipId} role="tooltip" className="block w-full rounded-r-sm border border-divider-subtle p-2">
          {gaps.map((gap, i) => <span key={i} className="block">{new Date(gap.from).toLocaleString()} — {new Date(gap.to).toLocaleString()}: {gap.reason}</span>)}
        </span>}
      </>}
      {ids.map((id) => (
        <Link key={id} to={companionEventHref(id)} title={`Open source event ${id}`} aria-label={`Open source event ${id}`}
          className="rounded-full border border-divider-subtle px-2 py-1 underline underline-offset-2 break-all">
          {compact ? `Event …${id.slice(-6)}` : id}
        </Link>
      ))}
      {raw && <span className="block w-full">Computed from {raw.computed_from}. Last projection: {raw.last_projection_at ? new Date(raw.last_projection_at).toLocaleString() : 'unknown'}.</span>}
      {!compact && <span className="block w-full">{answer?.limitation ?? 'Server-committed observations only. Offline entries and later corrections may change this answer; an empty record is not proof that no care happened.'}</span>}
    </span>
  )
}
